/**
 * Ligne « SKILLCARD + maîtrise » : créature origami dans la teinte de la carte,
 * titre, étoiles, barre de maîtrise, XP. Toute la ligne ouvre la page publique
 * de la carte (la page « classique », la même que le QR). Partagée par le
 * tableau de bord élève, la page « Mes SKILLCARDS » et la fiche élève du tuteur.
 */

import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { cn, MathText } from '@jeraisonne/ui';
import { OrigamiCreature } from '@jeraisonne/ui/tool-cards';
import {
  CARD_RARITY_LABELS,
  CARD_RARITY_STARS,
  cardPalette,
  stripMath,
} from '@jeraisonne/db/cards';
import {
  isSkillcardMastered,
  isSkillcardToWork,
  skillcardHora,
  type StudentSkillcard,
} from '@/lib/cards/student-skillcards';
import { HoraTrendBadge } from './hora-trend-badge';

export function SkillcardRow({
  card,
  /** Action à droite (ex. « Retirer » côté tuteur) — hors du lien. */
  action,
  /** Affiche qui a ajouté la carte (vue élève). */
  showAssigner = false,
}: {
  card: StudentSkillcard;
  action?: ReactNode;
  showAssigner?: boolean;
}) {
  const palette = cardPalette(card.levels, card.color_theme);
  const stars = CARD_RARITY_STARS[card.rarity];
  const mastered = isSkillcardMastered(card);
  const toWork = isSkillcardToWork(card);
  const worked = card.usage_count > 0;
  // HORA vivant (migration 0315) : érosion recalculée à l'affichage.
  const hora = skillcardHora(card);

  return (
    <div className="border-ink-200 flex items-stretch gap-2 rounded-2xl border bg-white">
      <Link
        to="/card/$cardId"
        params={{ cardId: card.id }}
        className="flex min-h-[2.75rem] min-w-0 flex-1 items-center gap-3 rounded-2xl p-3 transition-transform active:scale-[0.99]"
      >
        <span
          aria-hidden
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border-2"
          style={{ background: palette.bg, borderColor: palette.accent }}
        >
          <OrigamiCreature
            creature={card.creature}
            accent={palette.accent}
            evolved={hora.current >= card.xp_max && card.xp_max > 0}
            className="h-11 w-11"
          />
        </span>
        <span className="min-w-0 flex-1 space-y-1">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="text-ink-900 truncate text-sm font-bold">
              <MathText>{card.title}</MathText>
            </span>
            <span
              className="text-xs tracking-wider text-amber-500"
              aria-label={`Rareté : ${CARD_RARITY_LABELS[card.rarity]}`}
            >
              {'★'.repeat(stars)}
              <span className="text-ink-200">{'★'.repeat(5 - stars)}</span>
            </span>
          </span>
          <span className="flex items-center gap-2">
            <span
              role="progressbar"
              aria-label={`Maîtrise de ${stripMath(card.title)}`}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={card.mastery}
              className="bg-ink-100 h-2 flex-1 overflow-hidden rounded-full"
            >
              <span
                className="block h-full rounded-full"
                style={{ width: `${card.mastery}%`, background: palette.accent }}
              />
            </span>
            <span className="text-ink-700 w-10 shrink-0 text-right text-xs font-semibold">
              {worked ? `${card.mastery} %` : '—'}
            </span>
          </span>
          <span className="text-ink-500 flex flex-wrap items-center gap-x-2 text-xs">
            <span>
              {hora.current} / {card.xp_max} HORA
            </span>
            {worked && hora.trend !== 'stable' && <HoraTrendBadge status={hora} compact />}
            {mastered && (
              <span className="bg-xp-100 text-xp-700 rounded-full px-2 py-0.5 font-semibold">
                Maîtrisée
              </span>
            )}
            {toWork && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-700">
                À travailler
              </span>
            )}
            {showAssigner && card.assignment?.tutor_name && (
              <span>· ajoutée par {card.assignment.tutor_name}</span>
            )}
            {showAssigner && !card.assignment && card.diagnostic_flag && (
              <span>· repérée par ton diagnostic</span>
            )}
          </span>
        </span>
      </Link>
      {action && <div className="flex shrink-0 items-center pr-2">{action}</div>}
    </div>
  );
}

export function SkillcardList({
  cards,
  empty,
  renderAction,
  showAssigner,
}: {
  cards: StudentSkillcard[];
  empty: ReactNode;
  renderAction?: (card: StudentSkillcard) => ReactNode;
  showAssigner?: boolean;
}) {
  if (cards.length === 0) {
    return <p className={cn('text-ink-500 text-sm')}>{empty}</p>;
  }
  return (
    <ul className="space-y-2">
      {cards.map((c) => (
        <li key={c.id}>
          <SkillcardRow card={c} action={renderAction?.(c)} showAssigner={showAssigner} />
        </li>
      ))}
    </ul>
  );
}
